import {
    CustomClaimValue, Facility, FacilityCountingMode, FacilityOccupancy, getValidatedResponseData,
    GroundTruthCounter, GroundTruthJob, GroundTruthJobStatus, GroundTruths, IFacilityOccupancy,
    IGroundTruthJob, rawPost, Target,
} from "@app/shared";
import { OccupancyViewMode, RecentOccupancyTable } from "@app/shared/src/components/LiveView";
import { RecentTargetsTable } from "@app/shared/src/components/LiveView/RecentTargetsTable";
import { formatAgo } from "@app/shared/src/components_v2/formatters";
import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useMemo, useRef, useState } from "react";
import { toastError, toastSuccess } from "../../../helpers/toastHelpers";
import { useDialog, useFetch, useGroundTruthHub, useHasClaim, useParkingViewHub } from "../../../hooks";
import { Icons, LiveStatusChip, PageContainer } from "../../shared";
import { GroundTruthDebugDialog } from "./GroundTruthDebugDialog";
import { FacilityOccupancyOverlay } from "./parkingView/FacilityOccupancyOverlay";
import { GroundTruthPanel } from "./parkingView/GroundTruthPanel";
import { ParkingViewMapPanel } from "./parkingView/ParkingViewMapPanel";
import { ParkingViewUnconfigured } from "./parkingView/ParkingViewUnconfigured";
import { useParkingViewMode } from "./parkingView/useParkingViewMode";
import { TargetDetailsDialog } from "./TargetDetailsDialog";
import { GroundTruthEntryForm, GroundTruthEntryMode } from "./parkingView/GroundTruthEntryForm";

type FacilityParkingViewProps = {
    facility: Facility;
};

/**
 * Orchestrates the Parking View: works out how this location counts, then composes the panels that
 * suit it. The panels themselves live in ./parkingView — this file decides *which*, not *how*.
 *
 * Counting mode comes from the server, which derives it from each device's OperationMode (see
 * FacilityProvider.GetCountingMode). Superadmins can force any variant from the URL for testing —
 * see useParkingViewMode.
 */
export const FacilityParkingView: React.FC<FacilityParkingViewProps> = ({ facility }) => {
    const [hideOverflow, setHideOverflow] = useState(false);
    const [currentOccupancy, setCurrentOccupancy] = useState<FacilityOccupancy>();
    const details = useDialog<Target>();
    // Gates the occupancy table's loading state: until the hub delivers its first payload there is
    // no data *yet*, which is not the same as there being none. Destructured, so this is the flag
    // and not the [value, setter] tuple — as a tuple it was always truthy and the table skipped
    // straight to its empty state on first paint.
    const [hasFirstLiveResult, setHasFirstLiveResult] = useState(false);

    const [occupancy, , occupancyHelpers] = useFetch(() => GroundTruths.getCurrentOccupancy(facility.id), [facility.id]);

    // Which view this site gets is the server's call — it settles Hybrid against whether occupancy
    // data actually arrives, which the layout alone can't tell you. See ResolveCountingMode.
    const { mode, forceNoCoords, isOverridden } = useParkingViewMode(occupancy?.countingMode);

    // Ground truth and the count it feeds stay superadmin-only until the feature has settled; the
    // numbers are still being calibrated against hand counts and shouldn't drive customer
    // decisions yet. This file decides, and the panels honour it.
    const isSuperAdmin = useHasClaim(CustomClaimValue.SuperAdmin);

    // Both can be true at once, and that is a real configuration rather than an ambiguity to
    // resolve: the mode now reflects what each device is set to report, so a site showing both
    // tables genuinely runs a parking monitor and something else. See GetCountingMode.
    const showOccupancyTable = mode === FacilityCountingMode.Occupancy || mode === FacilityCountingMode.Hybrid;
    const showTargetsTable = mode === FacilityCountingMode.InOutDelta || mode === FacilityCountingMode.Hybrid;

    const timeZone = facility.localTimezoneId ?? Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).resolvedOptions().timeZone;
    const timeZoneLabel = useMemo(() => formatInTimeZone(new Date(), timeZone, "z"), [timeZone]);

    useParkingViewHub(facility.id, (occupancy: IFacilityOccupancy) => {
        setCurrentOccupancy(new FacilityOccupancy(occupancy));
        setHasFirstLiveResult(true);
    });

    // --- Ground truth + baseline ---
    const groundTruthDialog = useDialog<Facility>();
    const groundTruthDebugDialog = useDialog<string>();
    // Bumped to send the user to the ground truth panel's action and pulse it.
    const [highlightSignal, setHighlightSignal] = useState(0);

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [asOfDate, setAsOfDate] = useState<Date | null>(null);
    const [targetFilter, setTargetFilter] = useState<number>(0);
    const [entryMode, setEntryMode] = useState<GroundTruthEntryMode>("count");
    const [countEntry, setCountEntry] = useState<number | undefined>(undefined);
    const [counter, setCounter] = useState<GroundTruthCounter>(GroundTruthCounter.Default);

    // Superadmin only, and fetched rather than hardcoded so the model shown beside each counter is
    // whatever it would actually run. The endpoint refuses anyone else, so it is not even asked.
    const [counterOptions] = useFetch(
        () => (isSuperAdmin ? GroundTruths.getCounters() : Promise.resolve(undefined)), [isSuperAdmin]);

    // show()'s confirm callback closes over whatever these were when the button was clicked, before
    // anything was filled in — refs kept in sync are what let it read the latest at confirm time.
    const formRef = useRef({ selectedFiles, asOfDate, targetFilter, entryMode, countEntry, counter });
    useEffect(() => {
        formRef.current = { selectedFiles, asOfDate, targetFilter, entryMode, countEntry, counter };
    }, [selectedFiles, asOfDate, targetFilter, entryMode, countEntry, counter]);

    const [groundTruthJobs, , jobsHelpers] = useFetch(() => GroundTruths.getByFacility(facility.id), [facility.id]);

    // The hub is a "something changed" signal, not the data. Its payload is redacted for every
    // connection — a group has no single audience to answer for — while the HTTP read gives a
    // superadmin the real failure detail, so refetching keeps that rather than overwriting the
    // table with the safe version. Three pushes a run makes the extra round trips immaterial.
    //
    // Occupancy is refetched only when a run reaches Complete, since that is the only push that
    // can have moved the live number, and it is the expensive query of the two.
    useGroundTruthHub(facility.id, (jobs) => {
        const finishedNow = jobs.some(j =>
            j.status === GroundTruthJobStatus.Complete
            && !groundTruthJobs?.some(existing => existing.id === j.id && existing.status === GroundTruthJobStatus.Complete));
        jobsHelpers.refreshData();
        if (finishedNow) occupancyHelpers.refreshData();
    });

    const handleUpdateGroundTruthClick = () => {
        setSelectedFiles([]);
        setCountEntry(undefined);
        setEntryMode("count");
        setAsOfDate(new Date());
        setTargetFilter(Number(occupancy?.targetFilter ?? 0));
        setCounter(GroundTruthCounter.Default);
        groundTruthDialog.show("Update Ground Truth", "Save", facility, async () => {
            const {
                selectedFiles: files, asOfDate: date, targetFilter: filter, entryMode: entry,
                countEntry: typedCount, counter: chosenCounter,
            } = formRef.current;
            if (!date) throw new Error("Pick the moment this count was true.");

            if (entry === "count") {
                if (typedCount === undefined || typedCount < 0) throw new Error("Enter the number of vehicles on site.");
                await GroundTruths.setBaseline(facility.id, { count: typedCount, asOfDate: date, targetFilter: filter } as any);
                toastSuccess("Ground truth updated", `Counting from ${typedCount}.`);
                occupancyHelpers.refreshData();
                return;
            }

            if (files.length === 0) throw new Error("Select at least one photo.");

            // The generated upload() has no body parameter — the controller reads Request.Form
            // directly rather than a typed model, so the exporter can't see there's a body.
            const formData = new FormData();
            files.forEach(f => formData.append("photos", f));
            formData.append("asOfDate", date.toISOString());
            formData.append("targetFilter", String(filter));
            // Omitted for Default so the service applies its own configured counter rather than
            // being told one — and so an ordinary upload sends nothing the server would refuse.
            if (chosenCounter !== GroundTruthCounter.Default) {
                formData.append("counter", GroundTruthCounter[chosenCounter]);
            }

            // The /async route, not the synchronous one the integrations use: a run takes tens of
            // seconds, and holding the browser open for it means a spinner nobody can navigate away
            // from. This returns once the photos are stored; GroundTruthHub drives the table from
            // there, and the count lands in it when the run finishes.
            const response = await rawPost(`/api/groundtruth/${facility.id}/async`, formData);

            // rawPost is the one call here that does not go through apiFetch, so it gets none of
            // apiFetch's 401 handling: no re-auth attempt, no retry. Without this the dialog shows a
            // bare "Unauthorized" while the app still believes you are signed in — which is exactly
            // what an expired session looks like, and it looks like a broken upload instead.
            //
            // The server keeps the auth token in its own session, held in memory with the default
            // 20-minute idle timeout and no distributed store, so both a restart of CoreApi and
            // twenty idle minutes end the session while the browser still holds its cookie.
            if (response.status === 401) {
                document.dispatchEvent(new Event("auth:checkAuthState"));
                throw new Error("Your session has expired. Sign in again, then re-select the photos and retry.");
            }

            const job = await getValidatedResponseData<IGroundTruthJob>(response);
            if (!job) throw new Error("Upload failed — no response from the server.");

            // Immediate, so the queued row appears even if the hub is not connected.
            jobsHelpers.refreshData();

            toastSuccess(
                `Counting ${files.length} ${files.length === 1 ? "photo" : "photos"}`,
                "The result will appear in the ground truth table when it is done.");
        });
    };

    const handlePromote = async (job: GroundTruthJob) => {
        if (!job.id) return;
        try {
            await GroundTruths.promoteToBaseline(job.id);
            // The moment matters as much as the number. A baseline is measured *from* its as-of
            // date, so promoting an old run leaves the live count looking untouched — every
            // crossing since then is added straight back on top. Saying when it is from is what
            // makes an unchanged number make sense instead of looking like the click did nothing.
            toastSuccess(
                "Baseline updated",
                `Counting from ${job.totalCount} as of ${job.asOfDate
                    ? `${formatInTimeZone(job.asOfDate, timeZone, "MM/dd/yyyy h:mm aa zzz")} (${formatAgo(job.asOfDate)})`
                    : "the run's date"}.`);
            // The row's action retires once promoted, so the table has to be re-read too.
            jobsHelpers.refreshData();
            occupancyHelpers.refreshData();
        } catch (e) {
            toastError("Could not set baseline", e instanceof Error ? e.message : String(e));
        }
    };

    // Nothing to render until the server says how this site counts — see useParkingViewMode for
    // why there is no local guess to fall back on.
    if (mode === undefined) {
        return <div className="h-screen" />;
    }

    if (mode === FacilityCountingMode.Unknown) {
        return (
            <div className="h-screen flex flex-col">
                <ParkingViewUnconfigured />
                {isOverridden && <ParkingViewOverrideNotice />}
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col">
            <div className="h-[50%]">
                <ParkingViewMapPanel
                    facility={facility}
                    currentOccupancy={currentOccupancy}
                    occupancy={occupancy}
                    timeZone={timeZone}
                    onEstablishBaseline={() => setHighlightSignal(n => n + 1)}
                    forceNoCoords={forceNoCoords}
                    showOccupancy={isSuperAdmin}
                />
            </div>

            <div className="flex-grow min-h-0 flex flex-col gap-3">
                {showOccupancyTable &&
                    <PageContainer
                        title={
                            <div className="flex items-center gap-3">
                                <span>Occupancy Changes</span>
                                <LiveStatusChip isLive={true} srMessage="Occupancy updates live." />
                            </div>
                        }
                        Icon={Icons.Timeline}
                    >
                        <div className="h-full overflow-y-auto">
                            <RecentOccupancyTable
                                mode={OccupancyViewMode.Facility}
                                facility={facility}
                                data={currentOccupancy?.zoneOccupancy ?? []}
                                isLoading={!hasFirstLiveResult}
                            />
                        </div>
                    </PageContainer>
                }

                {showTargetsTable &&
                    <PageContainer
                        title={
                            <div className="flex items-center gap-3">
                                <span>Recent Targets</span>
                                <LiveStatusChip isLive={true} srMessage="Target list updates live." />
                            </div>
                        }
                        Icon={Icons.Target}
                    >
                        {/* No overflow-y-auto: SortableTable runs Virtuoso in infinite-scroll mode
                            and sizes its own viewport, so wrapping it in a scroller produced two
                            scrollbars. hidden clips it to the panel; Virtuoso does the scrolling. */}
                        <div className="h-full overflow-hidden">
                            <RecentTargetsTable
                                mode="facility"
                                facility={facility}
                                facilityId={facility.id}
                                devices={facility.devices ?? []}
                                timezoneId={facility.localTimezoneId}
                                onSelectedChange={() => { }}
                                onRowClick={(target) => details.show("Target", "Close", target)}
                                hideLaneAndTurn={true}
                                showFacilityDirection={true}
                            />
                        </div>
                    </PageContainer>
                }

                {isSuperAdmin && <GroundTruthPanel
                    facility={facility}
                    jobs={groundTruthJobs}
                    isLoading={jobsHelpers.isLoading}
                    timeZone={timeZone}
                    timeZoneLabel={timeZoneLabel}
                    onUpload={handleUpdateGroundTruthClick}
                    onShowDetail={(jobId) => groundTruthDebugDialog.show("Ground Truth Detail", "Close", jobId)}
                    onPromote={handlePromote}
                    highlightSignal={highlightSignal}
                />}

            </div>

            {isOverridden && <ParkingViewOverrideNotice />}

            {details.renderDialog((val) =>
                <TargetDetailsDialog facility={facility} target={val} onZoomChange={setHideOverflow} />
                , { closeOnOutsideClick: true, showCancelButton: false, className: hideOverflow ? "overflow-hidden max-h-screen w-full max-w-3xl" : undefined })}

            {groundTruthDialog.renderDialog(() =>
                <GroundTruthEntryForm
                    onModeChange={setEntryMode}
                    count={countEntry}
                    onCountChange={setCountEntry}
                    selectedFiles={selectedFiles}
                    onFilesChange={setSelectedFiles}
                    asOfDate={asOfDate}
                    onDateChange={setAsOfDate}
                    facilityTimezone={facility.localTimezoneId}
                    timeZoneLabel={timeZoneLabel}
                    facilityName={facility.name ?? "this location"}
                    facilityTargetFilter={Number(occupancy?.targetFilter ?? 0)}
                    targetFilter={targetFilter}
                    onTargetFilterChange={setTargetFilter}
                    counterOptions={counterOptions}
                    counter={counter}
                    onCounterChange={setCounter}
                />
                , { closeOnOutsideClick: false })}

            {groundTruthDebugDialog.renderDialog((jobId) =>
                <GroundTruthDebugDialog jobId={jobId!} />
                , { closeOnOutsideClick: true, className: "w-full max-w-6xl" })}
        </div>
    );
};

/** Visible marker that what's on screen came from a URL override, not this facility's real layout. */
const ParkingViewOverrideNotice: React.FC = () => (
    <div className="fixed bottom-3 right-3 z-50 rounded bg-amber-100 text-amber-900 border border-amber-300 px-3 py-2 text-xs shadow">
        Previewing a forced Parking View mode (superadmin URL override) — not this location's real configuration.
    </div>
);
